/**
 * 页面上下文 Store（领域层，无 UI）
 * ------------------------------------------------------------
 * 平台级机制：页面挂载时 setPage 登记，卸载时 clearPage 注销；
 * 页面内切换查看对象时 patchPage 局部更新。
 * 未登记的页面由路由兜底：按路径前缀推断业务域 + meta.title 作节点，
 * 保证右栏在任何页面都有如实的基本显示，不留空白、不编造内容。
 */
import { defineStore } from 'pinia'
import { computed, shallowRef } from 'vue'
import { useRoute } from 'vue-router'
import type { PageContext } from './types'

/** 路径前缀 → 业务主线（兜底用，与路由表保持同步；新增顶级业务页时在此补一行） */
const LINE_BY_PREFIX: Array<[string, string]> = [
  ['/chat', '查一查'],
  ['/apps', '应用中心'],
  ['/product', '产研'],
  ['/procurement', '采购'],
  ['/tasks', '任务'],
  ['/arch', '架构'],
  ['/fde', 'FDE'],
  ['/sys', '平台配置'],
  ['/knowledge', '在线知识'],
  ['/messenger', '消息'],
  ['/mail', '邮件'],
  ['/calendar', '日程'],
  ['/cloud-space', '云空间'],
  ['/cloud-pc', '云电脑'],
  ['/profile', '我的'],
  ['/automation', '自动化'],
  ['/workers', '打工人'],
  ['/components', '组件库'],
  ['/docs', '文档'],
  ['/help', '帮助'],
]

export const usePageContextStore = defineStore('page-context', () => {
  const route = useRoute()
  const declared = shallowRef<PageContext | null>(null)
  let token = 0

  /** 页面挂载时登记；返回的句柄用于 patch / clear，防止上一页的注销误清下一页 */
  function setPage(ctx: PageContext) {
    const t = ++token
    declared.value = ctx
    return t
  }
  function patchPage(t: number, patch: Partial<PageContext>) {
    if (t !== token || !declared.value) return
    declared.value = { ...declared.value, ...patch }
  }
  function clearPage(t: number) {
    if (t !== token) return
    declared.value = null
  }

  /** 路由兜底：业务域取自前缀表（事实），节点取 meta.title（路由表事实），其余留空 */
  const fallback = computed<PageContext>(() => {
    const hit = LINE_BY_PREFIX.find(([p]) => route.path === p || route.path.startsWith(p + '/'))
    return {
      line: hit?.[1] || '平台',
      node: (route.meta.title as string | undefined) || route.name?.toString() || route.path,
    }
  })

  /** 右栏实际使用：页面登记优先，未登记录兜底 */
  const current = computed<PageContext>(() => declared.value ?? fallback.value)
  /** 是否为页面显式登记（false = 兜底，右栏可据此提示「该页尚未接入」） */
  const isDeclared = computed(() => declared.value !== null)

  return { current, isDeclared, setPage, patchPage, clearPage }
})
